import {
    PGCSearchableFileSortInput,
    PGCSearchableFileSortableFields,
    PGCSearchableSortDirection,
} from '@/API';
import { getSetting } from "@/utils/AccountSettingUtils";

/**
 * ソート対象画面のキー定数
 */
export const SORT_ORDER_KEYS = {
    /** ファイル検索結果画面のソート対象画面キー */
    FILE_SEARCH_RESULT: 'fileSearchResultSortOrder',
    /** アルバム内ファイル一覧画面のソート対象画面キー */
    FILE_GROUP_DETAIL: 'fileGroupDetailSortOrder',
} as const;

/**
 * ソート条件のデフォルト値
 */
export const DEFAULT_SORT_ORDER = 'createdAt_desc';

/**
 * ソート条件の選択肢（UI表示用）
 */
export const SORT_ITEMS = [
    { text: '登録日(新しい順)', value: 'createdAt_desc' },
    { text: '登録日(古い順)', value: 'createdAt_asc' },
    { text: '撮影日(新しい順)', value: 'shootingAt_desc' },
    { text: '撮影日(古い順)', value: 'shootingAt_asc' },
] as const;

/**
 * ソート条件が有効かどうかを検証する
 * 
 * @param sortOrder - 検証するソート条件
 * @returns 有効な場合はtrue、無効な場合はfalse
 */
export function validateSortOrder(sortOrder: string): boolean {
    return SORT_ITEMS.some(item => item.value === sortOrder);
}

/**
 * ソート条件の文字列からファイル検索の GraphQL のソート入力オブジェクトを作成
 *
 * @param sortOrder - ソート条件の文字列（例: DEFAULT_SORT_ORDER）
 * @returns ファイル検索の GraphQL の sort オブジェクト
 *
 * sortOrderのフォーマット: {field}_{direction}
 * - field: createdAt, shootingAt
 * - direction: asc, desc
 */
export function createSortInput(sortOrder: string): PGCSearchableFileSortInput {
    // デフォルト値を設定
    const defaultSort = {
        field: PGCSearchableFileSortableFields.createdAt,
        direction: PGCSearchableSortDirection.desc,
    };

    // sortOrderが空の場合はデフォルトを返す
    if (!sortOrder) {
        return defaultSort;
    }

    // 最後の'_'の位置を取得
    const lastUnderscorePosition = sortOrder.lastIndexOf('_');
    if (lastUnderscorePosition <= 0) {
        return defaultSort;
    }

    const field = sortOrder.slice(0, lastUnderscorePosition);
    const direction = sortOrder.slice(lastUnderscorePosition + 1);

    // enumに含まれているかチェック
    const isValidField = Object.values(PGCSearchableFileSortableFields).includes(
        field as PGCSearchableFileSortableFields
    );
    const isValidDirection = Object.values(PGCSearchableSortDirection).includes(
        direction as PGCSearchableSortDirection
    );

    // 不正な値の場合はデフォルトを返す
    if (!isValidField || !isValidDirection) {
        return defaultSort;
    }

    // 正常な場合はenum型として返す
    return {
        field: field as PGCSearchableFileSortableFields,
        direction: direction as PGCSearchableSortDirection,
    };
}

/**
 * ソート条件を localStorage に保存
 *
 * @param key - ソート対象画面のキー
 * @param sortOrder - ソート条件の文字列
 */
export function setSortOrder(key: string, sortOrder: string): void {
    localStorage.setItem(key, sortOrder);
}

/**
 * ソート条件の文字列を取得し、必要に応じて初期化する
 *
 * @param key - ソート対象画面のキー（例: 'fileSearchResultSortOrder', 'fileGroupDetailSortOrder'）
 * @returns ソート条件の文字列（例: DEFAULT_SORT_ORDER）
 */
export async function getOrInitializeSortOrder(key: string): Promise<string> {
    // 1. localStorage の指定されたキーから取得してバリデーション
    const localStorageSort = localStorage.getItem(key);
     // 正常な値の場合はそのまま返す
    if (localStorageSort && validateSortOrder(localStorageSort)) {
        return localStorageSort;
    }

    // 2. account.config.defaultSortOrder の指定されたキー（検証後localStorageに保存）
    const accountSetting = await getSetting();
    if (!accountSetting?.defaultSortOrder) {
        // accountSetting.defaultSortOrderが存在しない場合は、デフォルト値をlocalStorageに保存して返す
        setSortOrder(key, DEFAULT_SORT_ORDER);
        return DEFAULT_SORT_ORDER;
    }
    try {
        const sortOrder = accountSetting.defaultSortOrder?.[key];

        if (sortOrder) {
            const sortOrderStr = `${sortOrder.field}_${sortOrder.direction}`;
            // accountSettingの値を検証
            if (validateSortOrder(sortOrderStr)) {
                // localStorageに保存して返す
                setSortOrder(key, sortOrderStr);
                return sortOrderStr;
            }
        }
    } catch (error) {
        console.error('Failed to parse account config:', error);
    }

    // 3. デフォルト値 (DEFAULT_SORT_ORDER)（localStorageに保存）
    setSortOrder(key, DEFAULT_SORT_ORDER);
    return DEFAULT_SORT_ORDER;
}
